import { forwardRef, useEffect, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { QuietAction } from './PrimaryAction';

/**
 * MY-WORK-PRIMITIVE-CONFORMANCE-V6 -- MasterDetail: THE master-detail
 * layout as a primitive, measured from the founder-approved visual
 * reference (docs/design/mockups/my-work-c-system-2026-09-03.html).
 *
 * Anatomy (standard s.2 + s.2A, the four planes): on the working ground a
 * MASTER card (elevation 1: head / body / foot -- the filters, the register
 * scrolling INSIDE the card, the count and freshness) beside a condensed
 * DETAIL card (elevation 2: head / body / foot); inside the detail a WORK
 * region (brand-tinted, brand-edged, dominant -- the one thing the operator
 * came for) above subordinate REFERENCE regions (sunken) and a timeline.
 * Both cards fill the workspace so their bottoms align.
 *
 * MY-WORK-RECOMPOSE-V4 composed this anatomy as CSS classes inside one
 * screen (and bridged the selected row's wash into the detail); the
 * reference carries no bridge, so it is gone, and the markup is owned HERE
 * so the next queue-shaped screen (a worklist, a day board) inherits it.
 * PRESENTATION ONLY: nothing here fetches, selects or writes.
 */
export interface MasterDetailProps {
  /** No detail column (the caller holds no read for it): one column. */
  readonly single?: boolean;
  readonly children: ReactNode;
  readonly testId?: string;
}

export default function MasterDetail({ single, children, testId }: MasterDetailProps): JSX.Element {
  return (
    <div
      className={single ? 'ac-masterdetail ac-masterdetail--single' : 'ac-masterdetail'}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export interface MasterProps {
  readonly label: string;
  readonly head?: ReactNode;
  readonly foot?: ReactNode;
  readonly children: ReactNode;
  readonly testId?: string;
  readonly headTestId?: string;
  readonly bodyTestId?: string;
  readonly footTestId?: string;
  readonly onKeyDown?: (e: KeyboardEvent<HTMLElement>) => void;
}

/** The MASTER card: head (filters) / body (the register) / foot (count). */
export const Master = forwardRef<HTMLElement, MasterProps>(function Master(
  { label, head, foot, children, testId, headTestId, bodyTestId, footTestId, onKeyDown },
  ref,
) {
  return (
    <section className="ac-master" aria-label={label} data-testid={testId} ref={ref} onKeyDown={onKeyDown}>
      {head !== undefined && head !== null ? (
        <div className="ac-master__head" data-testid={headTestId}>
          {head}
        </div>
      ) : null}
      <div className="ac-master__body" data-testid={bodyTestId}>
        {children}
      </div>
      {foot !== undefined && foot !== null ? (
        <div className="ac-master__foot" data-testid={footTestId}>
          {foot}
        </div>
      ) : null}
    </section>
  );
});

export interface DetailAsideProps {
  readonly label: string;
  readonly children: ReactNode;
  readonly testId?: string;
}

/** The detail COLUMN of the grid (a flex column the detail card fills). */
export function DetailAside({ label, children, testId }: DetailAsideProps): JSX.Element {
  return (
    <aside className="ac-masterdetail__aside" aria-label={label} data-testid={testId}>
      {children}
    </aside>
  );
}

export interface DetailProps {
  readonly label: string;
  readonly head?: ReactNode;
  readonly foot?: ReactNode;
  readonly children: ReactNode;
  readonly testId?: string;
  readonly busy?: boolean;
  /** Stamped on the section (a host's frame marker, e.g. data-frame). */
  readonly frame?: string;
}

/** The DETAIL card: head / body / foot, the primary work at elevation 2. */
export function Detail({ label, head, foot, children, testId, busy, frame }: DetailProps): JSX.Element {
  return (
    <section
      className="ac-detail"
      aria-label={label}
      aria-busy={busy === true ? true : undefined}
      data-testid={testId}
      data-frame={frame}
    >
      {head !== undefined && head !== null ? <div className="ac-detail__head">{head}</div> : null}
      <div className="ac-detail__body">{children}</div>
      {foot !== undefined && foot !== null ? <div className="ac-detail__foot">{foot}</div> : null}
    </section>
  );
}

export interface RegionProps {
  readonly eyebrow?: ReactNode;
  readonly children: ReactNode;
  readonly testId?: string;
}

/** The WORK region: brand-tinted, brand-edged, the one thing to do now. */
export function WorkRegion({ eyebrow, title, children, testId }: RegionProps & { readonly title?: ReactNode }): JSX.Element {
  return (
    <div className="ac-region ac-region--work" data-testid={testId}>
      {eyebrow !== undefined && eyebrow !== null ? <span className="ac-region__eyebrow">{eyebrow}</span> : null}
      {title !== undefined && title !== null ? <h3 className="ac-region__title">{title}</h3> : null}
      {children}
    </div>
  );
}

/** A REFERENCE region: sunken, subordinate; `grow` lets the last one fill. */
export function ReferenceRegion({ eyebrow, children, testId, grow }: RegionProps & { readonly grow?: boolean }): JSX.Element {
  return (
    <div
      className={grow ? 'ac-region ac-region--reference ac-region--grow' : 'ac-region ac-region--reference'}
      data-testid={testId}
    >
      {eyebrow !== undefined && eyebrow !== null ? <span className="ac-region__eyebrow">{eyebrow}</span> : null}
      {children}
    </div>
  );
}

/** Work-panel chrome composed from the existing detail card. No modal semantics
 * or scrim: the owning workspace keeps its list available beside this region. */
export function WorkPanel({ title, eyebrow, onClose, closeDisabled = false, children, footer, notice, testId }: {
  readonly title: string; readonly eyebrow?: string; readonly onClose: () => void;
  readonly closeDisabled?: boolean; readonly children: ReactNode;
  readonly footer?: ReactNode; readonly notice?: ReactNode; readonly testId?: string;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return <div className="ac-work-panel" ref={ref} tabIndex={-1} data-testid={testId}
    onKeyDown={(event) => {
      if (event.key === 'Escape' && !closeDisabled) { event.stopPropagation(); onClose(); }
    }}>
    <Detail label={title} head={<><div className="ac-work-panel__heading">
      <div>{eyebrow ? <p className="ac-region__eyebrow">{eyebrow}</p> : null}
        <h2 className="ac-detail__title">{title}</h2></div>
      <QuietAction size="sm" onClick={onClose} disabled={closeDisabled}>Close</QuietAction>
    </div>{notice}</>} foot={footer}>{children}</Detail>
  </div>;
}
